<template>
  <div class="security-document-request-detail">
    <v-container>
      <v-layout row wrap>
        <v-flex xs12>
          <v-btn flat color="primary" @click="$router.back()">
            <v-icon left>arrow_back</v-icon>
            Volver
          </v-btn>
        </v-flex>
      </v-layout>

      <!-- Header con info de la solicitud -->
      <v-layout row wrap class="mt-4" v-if="requestDetail">
        <v-flex xs12>
          <v-card class="elevation-2">
            <v-card-title>
              <v-layout row wrap align-center>
                <v-flex xs12 sm6>
                  <h2>{{ requestDetail.document_type_display || requestDetail.document_type }}</h2>
                </v-flex>
                <v-flex xs12 sm6 text-xs-right>
                  <v-chip
                    :color="getStatusColor(requestDetail.status)"
                    text-color="white"
                    large
                  >
                    {{ requestDetail.status_display || requestDetail.status }}
                  </v-chip>
                </v-flex>
              </v-layout>
            </v-card-title>

            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 sm6>
                  <p><strong>Descripción:</strong></p>
                  <p>{{ requestDetail.description || 'Sin descripción' }}</p>
                </v-flex>
                <v-flex xs12 sm6>
                  <p><strong>Información:</strong></p>
                  <p>Creado: {{ formatDate(requestDetail.created_at) }}</p>
                  <p>Fecha Límite: {{ formatDate(requestDetail.deadline) || '-' }}</p>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Resumen de estado -->
      <v-layout row wrap class="mt-4" v-if="summary">
        <v-flex xs12 sm3>
          <v-card class="text-xs-center elevation-1">
            <v-card-text>
              <h3>{{ summary.total_guards }}</h3>
              <p class="grey--text">Total de Guardias</p>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs12 sm3>
          <v-card class="text-xs-center elevation-1 green lighten-5">
            <v-card-text>
              <h3 class="green--text">{{ summary.submitted }}</h3>
              <p class="grey--text">Entregados</p>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs12 sm3>
          <v-card class="text-xs-center elevation-1 orange lighten-5">
            <v-card-text>
              <h3 class="orange--text">{{ summary.pending }}</h3>
              <p class="grey--text">Pendientes</p>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs12 sm3>
          <v-card class="text-xs-center elevation-1 red lighten-5">
            <v-card-text>
              <h3 class="red--text">{{ summary.rejected }}</h3>
              <p class="grey--text">Rechazados</p>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Tabla de Guardias y sus submissions -->
      <v-layout row wrap class="mt-4">
        <v-flex xs12>
          <v-card class="elevation-2">
            <v-card-title>
              Estado de Entregas
              <v-spacer></v-spacer>
              <v-text-field
                v-model="searchGuards"
                append-icon="search"
                label="Buscar guardia"
                single-line
                hide-details
                class="mr-3"
              ></v-text-field>
            </v-card-title>

            <v-data-table
              :headers="submissionsHeaders"
              :items="submissions"
              :search="searchGuards"
              :loading="isLoading"
              :rows-per-page-text="$t('filas_por_pagina')"
              :rows-per-page-items="[
                {text: '5', value: 5},
                {text: '10', value: 10},
                {text: '25', value: 25},
                {text: this.$t('todos'), value: -1}
              ]"
              class="elevation-1"
            >
              <template slot="items" slot-scope="props">
                <td class="text-xs-center">
                  <v-icon
                    v-if="props.item.status === 'APPROVED'"
                    color="green"
                    medium
                  >
                    check_circle
                  </v-icon>
                  <v-icon
                    v-else-if="props.item.status === 'REJECTED'"
                    color="red"
                    medium
                  >
                    cancel
                  </v-icon>
                  <v-icon
                    v-else
                    color="orange"
                    medium
                  >
                    schedule
                  </v-icon>
                </td>
                <td>{{ props.item.guard_name || props.item.guard }}</td>
                <td>
                  <v-chip
                    :color="getStatusColor(props.item.status)"
                    text-color="white"
                    small
                  >
                    {{ props.item.status_display || props.item.status }}
                  </v-chip>
                </td>
                <td>{{ formatDate(props.item.submitted_at) || '-' }}</td>
                <td>{{ formatDate(props.item.reviewed_at) || '-' }}</td>
                <td class="text-xs-center">
                  <v-icon
                    v-if="props.item.status === 'PENDING' || props.item.status === 'REJECTED'"
                    medium
                    color="primary"
                    @click="openReviewDialog(props.item)"
                    style="cursor: pointer;"
                  >
                    visibility
                  </v-icon>
                  <span v-else>-</span>
                </td>
              </template>
              <template v-slot:no-results>
                <v-alert :value="true" color="info" icon="info">
                  Sin guardias para mostrar
                </v-alert>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- DIALOG: Revisar y Aprobar/Rechazar -->
    <v-dialog v-model="showReviewDialog" max-width="700px" persistent>
      <v-card v-if="selectedSubmission" class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>Revisar Documento</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" flat @click="showReviewDialog = false" class="align-self-end">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-layout row wrap>
            <v-flex xs12>
              <p><strong>Guardia:</strong> {{ selectedSubmission.guard_name }}</p>
              <p><strong>Tipo de Documento:</strong> {{ selectedSubmission.document_type }}</p>
              <p><strong>Entregado:</strong> {{ formatDate(selectedSubmission.submitted_at) || 'No entregado' }}</p>
            </v-flex>

            <v-flex xs12 v-if="selectedSubmission.file_url">
              <v-divider class="my-3"></v-divider>
              <p><strong>Archivo (URL externa):</strong></p>
              <v-btn
                color="primary"
                target="_blank"
                :href="selectedSubmission.file_url"
              >
                <v-icon left>link</v-icon>
                Ver Archivo
              </v-btn>
            </v-flex>

            <v-flex xs12 v-if="selectedSubmission.status === 'REJECTED'">
              <v-divider class="my-3"></v-divider>
              <p><strong>Motivo del Rechazo:</strong></p>
              <p class="red--text">{{ selectedSubmission.rejection_reason }}</p>
            </v-flex>

            <!-- Acciones solo si está PENDING o REJECTED -->
            <v-flex xs12 v-if="selectedSubmission.status === 'PENDING' || selectedSubmission.status === 'REJECTED'">
              <v-divider class="my-3"></v-divider>
              <p><strong>Acción:</strong></p>
              <v-layout row wrap>
                <v-flex xs12>
                  <v-radio-group v-model="reviewAction">
                    <v-radio label="Aprobar" value="approve"></v-radio>
                    <v-radio label="Rechazar" value="reject"></v-radio>
                  </v-radio-group>
                </v-flex>

                <v-flex xs12 v-if="reviewAction === 'reject'">
                  <v-textarea
                    v-model="rejectionReason"
                    label="Motivo del Rechazo *"
                    rows="3"
                    placeholder="Ej: Imagen borrosa, no se lee el documento"
                  ></v-textarea>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions v-if="selectedSubmission.status === 'PENDING' || selectedSubmission.status === 'REJECTED'">
          <v-spacer></v-spacer>
          <v-btn flat @click="showReviewDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="submitReview"
            :loading="isLoading"
          >
            {{ reviewAction === 'approve' ? 'Aprobar' : 'Rechazar' }}
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else>
          <v-spacer></v-spacer>
          <v-btn flat @click="showReviewDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  FETCH_DOCUMENT_REQUEST_DETAIL,
  APPROVE_DOCUMENT_SUBMISSION,
  REJECT_DOCUMENT_SUBMISSION
} from '@/store/modules/security'
import { getStatusColor } from '@/utils/documentRequestHelpers'

export default {
  name: 'security_document_request_detail',
  computed: {
    ...mapGetters('security', {
      isLoading: 'isSecurityLoading',
      securityError: 'getSecurityError',
      requestDetail: 'getDocumentRequestDetail'
    }),
    submissions() {
      return (this.requestDetail && this.requestDetail.submissions) || []
    },
    summary() {
      return (this.requestDetail && this.requestDetail.summary) || null
    }
  },
  data() {
    return {
      searchGuards: '',
      showReviewDialog: false,
      selectedSubmission: null,
      reviewAction: 'approve',
      rejectionReason: '',
      submissionsHeaders: [
        { text: 'Estado', value: 'status', align: 'center' },
        { text: 'Guardia', value: 'guard_name' },
        { text: 'Status', value: 'status_display' },
        { text: 'Entregado', value: 'submitted_at' },
        { text: 'Revisado', value: 'reviewed_at' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ]
    }
  },
  mounted() {
    this.fetchDetail()
  },
  methods: {
    ...mapActions('security', [
      FETCH_DOCUMENT_REQUEST_DETAIL,
      APPROVE_DOCUMENT_SUBMISSION,
      REJECT_DOCUMENT_SUBMISSION
    ]),
    fetchDetail() {
      const token = this.$store.getters.getToken
      const requestId = this.$route.params.requestId
      this[FETCH_DOCUMENT_REQUEST_DETAIL]({
        token,
        requestId
      })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al cargar los detalles')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    openReviewDialog(submission) {
      this.selectedSubmission = submission
      this.reviewAction = 'approve'
      this.rejectionReason = ''
      this.showReviewDialog = true
    },
    submitReview() {
      const token = this.$store.getters.getToken

      if (this.reviewAction === 'reject' && !this.rejectionReason.trim()) {
        this.$store.commit('snackbar_change_message', 'Por favor ingrese el motivo del rechazo')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
        return
      }

      if (this.reviewAction === 'approve') {
        this[APPROVE_DOCUMENT_SUBMISSION]({
          token,
          submissionId: this.selectedSubmission.id
        })
          .then(() => {
            this.$store.commit('snackbar_change_message', 'Documento aprobado')
            this.$store.commit('snackbar_change_color', 'success')
            this.$store.commit('snackbar_activate_true')
            this.showReviewDialog = false
            this.fetchDetail()
          })
          .catch(() => {
            this.$store.commit('snackbar_change_message', this.securityError || 'Error al aprobar documento')
            this.$store.commit('snackbar_change_color', 'error')
            this.$store.commit('snackbar_activate_true')
          })
      } else {
        this[REJECT_DOCUMENT_SUBMISSION]({
          token,
          submissionId: this.selectedSubmission.id,
          rejectionReason: this.rejectionReason
        })
          .then(() => {
            this.$store.commit('snackbar_change_message', 'Documento rechazado')
            this.$store.commit('snackbar_change_color', 'warning')
            this.$store.commit('snackbar_activate_true')
            this.showReviewDialog = false
            this.fetchDetail()
          })
          .catch(() => {
            this.$store.commit('snackbar_change_message', this.securityError || 'Error al rechazar documento')
            this.$store.commit('snackbar_change_color', 'error')
            this.$store.commit('snackbar_activate_true')
          })
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('es-MX')
      } catch (e) {
        return dateStr
      }
    },
    getStatusColor
  }
}
</script>

<style scoped>
.security-document-request-detail {
  padding: 20px;
}
</style>
